import { useGlobalNotifier, useSpreadsheetState } from "@vrf/fr-vrf-core-react";
import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import {
  FrVrfButton,
  FrVrfFooter,
  FrVrfTitle,
  FrVrfTextDisplayField,
} from "@vrf/fr-vrf-web-components-react";
import { isDisplayedAction } from "@vrf/fr-vrf-auth-core";
import React, { useEffect, useMemo } from "react";
import { createPath, useLocation, useNavigate } from "react-router-dom";
import {
  CnptSpreadsheetRecordData,
  IfSpreadsheetRecordData,
  ProjectDetailPageInitialState,
} from ".";
import { useAppState } from "../../app";
import {
  InterfaceDefinitionDetail,
  useGetProject,
  useGetProjectTickets,
} from "../../services/project";
import { ENV } from "@vrf/fr-vrf-core";
import { usePageState } from "./usePageState";
import {
  PjtLnchType,
  ReferenceModeType,
} from "../../util/general-code/enums/general-codes";
const TEMPLATE_INTERFACE_PROGRESS_SUMMARY = `spreadsheet-template/project-detail-interface-progress-summary.xlsx`;
const TEMPLATE_COMPONENT_PROGRESS_SUMMARY = `spreadsheet-template/project-detail-component-progress-summary.xlsx`;

export const ProjectDetailPageContent = () => {
  const dfnSetVerNum = ENV("REACT_APP_DFN_SET_VER_NUM");
  const navigate = useNavigate();
  const search = useLocation().search;
  const queryParameter = new URLSearchParams(search);
  const { resetUserError } = useGlobalNotifier();
  const [ifSpreadsheetState, setIfSpreadsheetState, initIfSpreadSheet] =
    useSpreadsheetState<{
      ifSpreadSheet: IfSpreadsheetRecordData[];
    }>();
  const [cpntSpreadsheetState, setCpntSpreadsheetState, initCpntSpreadSheet] =
    useSpreadsheetState<{
      cpntSpreadSheet: CnptSpreadsheetRecordData[];
    }>();
  const params = {
    pjtId: queryParameter.get("pjtId") ?? "",
  };
  const [getProjectResponse, getProjectRequest] = useGetProject();
  const [getProjectTicketsResponse, getProjectTicketsRequest] =
    useGetProjectTickets();
  const [appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();

  const onInitialDisplayed = (msg?: string): void => {
    resetUserError();
    if (params.pjtId !== "") {
      getProjectRequest({
        pjtId: params.pjtId,
        dfnSetVerNum: dfnSetVerNum,
      });
      getProjectTicketsRequest({
        pjtId: params.pjtId,
        dfnSetVerNum: dfnSetVerNum,
      });
      setAppState({
        ...appState,
        title: `Project Detail`,
        pjtId: params.pjtId,
      });
    }
  };
  useEffect(onInitialDisplayed, []);

  const onListenGetProjectResponse = (): void => {
    if (!getProjectResponse) return;

    setPageState((prevState) => ({
      ...prevState,
      projects: {
        ...getProjectResponse,
      },
    }));

    const pageName = `Project Name: ${getProjectResponse.projectInformation.pjtName}`;
    setAppState({
      ...appState,
      title: `Project Detail ( ${pageName} )`,
    });
  };
  useEffect(onListenGetProjectResponse, [getProjectResponse]);

  const onListenGetProjectTicketsResponse = (): void => {
    if (!getProjectTicketsResponse) return;

    setPageState((prevState) => ({
      ...prevState,
      projectTickets: {
        ...getProjectTicketsResponse,
      },
    }));
  };
  useEffect(onListenGetProjectTicketsResponse, [getProjectTicketsResponse]);

  const ifSpreadsheetInitialData = useMemo(() => {
    if (pageState.projectTickets.interfaceDefinitions.length === 0) {
      return {
        ifSpreadSheet: [
          {
            ...ProjectDetailPageInitialState.ifSpreadSheet.tmplateRow,
            tag: "ALL",
            stgDplymntPlnDate: "ALL",
            prdDplymntPlnDate: "ALL",
            stsStgDplymntPlanningBlocked: "(0 *)",
            stsPrdDplymntPlanningBlocked: "(0 *)",
          },
        ],
      };
    }
    const getAll = (
      groups: InterfaceDefinitionDetail[],
    ): InterfaceDefinitionDetail => {
      let all: InterfaceDefinitionDetail = {
        tag: "ALL",
        stgDplymntPlnDate: "ALL",
        prdDplymntPlnDate: "ALL",
        total: 0,
        stsDsgnDesigning: 0,
        stsDsgnReviewing: 0,
        stsStgDplymntPlanning: 0,
        stsStgDplymntPlanningBlocked: 0,
        stsStgDplymntAwaitingApproval: 0,
        stsStgDplymntPlanned: 0,
        stsPrdDplymntPlanning: 0,
        stsPrdDplymntPlanningBlocked: 0,
        stsPrdDplymntAwaitingApproval: 0,
        stsPrdDplymntPlanned: 0,
        stsCompleted: 0,
      };

      groups.forEach((e) => {
        all.total += e.total;
        all.stsDsgnDesigning += e.stsDsgnDesigning;
        all.stsDsgnReviewing += e.stsDsgnReviewing;
        all.stsStgDplymntPlanning += e.stsStgDplymntPlanning;
        all.stsStgDplymntPlanningBlocked += e.stsStgDplymntPlanningBlocked;
        all.stsStgDplymntAwaitingApproval += e.stsStgDplymntAwaitingApproval;
        all.stsStgDplymntPlanned += e.stsStgDplymntPlanned;
        all.stsPrdDplymntPlanning += e.stsPrdDplymntPlanning;
        all.stsPrdDplymntPlanningBlocked += e.stsPrdDplymntPlanningBlocked;
        all.stsPrdDplymntAwaitingApproval += e.stsPrdDplymntAwaitingApproval;
        all.stsPrdDplymntPlanned += e.stsPrdDplymntPlanned;
        all.stsCompleted += e.stsCompleted;
      });

      return all;
    };

    const ifSpreadsheetRecordData: IfSpreadsheetRecordData[] = [
      getAll(pageState.projectTickets.interfaceDefinitions),
      ...pageState.projectTickets.interfaceDefinitions,
    ].map((e) => {
      let ifResult: IfSpreadsheetRecordData = JSON.parse(
        JSON.stringify(ProjectDetailPageInitialState.ifSpreadSheet.tmplateRow),
      );
      ifResult.tag = e.tag ?? "";
      ifResult.stgDplymntPlnDate = e.stgDplymntPlnDate;
      ifResult.prdDplymntPlnDate = e.prdDplymntPlnDate;
      ifResult.total = e.total.toString();
      ifResult.stsDsgnDesigning =
        e.stsDsgnDesigning === 0 ? "Done" : e.stsDsgnDesigning.toString();
      ifResult.stsDsgnReviewing =
        ifResult.stsDsgnDesigning === "Done" && e.stsDsgnReviewing === 0
          ? "Done"
          : e.stsDsgnReviewing.toString();
      ifResult.stsStgDplymntPlanning =
        ifResult.stsDsgnReviewing === "Done" && e.stsStgDplymntPlanning === 0
          ? "Done"
          : e.stsStgDplymntPlanning.toString();
      ifResult.stsStgDplymntPlanningBlocked =
        ifResult.stsStgDplymntPlanning === "Done"
          ? " "
          : "(" + e.stsStgDplymntPlanningBlocked.toString() + " *)";
      ifResult.stsStgDplymntAwaitingApproval =
        ifResult.stsStgDplymntPlanning === "Done" &&
        e.stsStgDplymntAwaitingApproval === 0
          ? "Done"
          : e.stsStgDplymntAwaitingApproval.toString();
      ifResult.stsStgDplymntPlanned =
        ifResult.stsStgDplymntAwaitingApproval === "Done" &&
        e.stsStgDplymntPlanned === 0
          ? "Done"
          : e.stsStgDplymntPlanned.toString();
      ifResult.stsPrdDplymntPlanning =
        ifResult.stsStgDplymntPlanned === "Done" &&
        e.stsPrdDplymntPlanning === 0
          ? "Done"
          : e.stsPrdDplymntPlanning.toString();
      ifResult.stsPrdDplymntPlanningBlocked =
        ifResult.stsPrdDplymntPlanning === "Done"
          ? " "
          : "(" + e.stsPrdDplymntPlanningBlocked.toString() + " *)";
      ifResult.stsPrdDplymntAwaitingApproval =
        ifResult.stsPrdDplymntPlanning === "Done" &&
        e.stsPrdDplymntAwaitingApproval === 0
          ? "Done"
          : e.stsPrdDplymntAwaitingApproval.toString();
      ifResult.stsPrdDplymntPlanned =
        ifResult.stsPrdDplymntAwaitingApproval === "Done" &&
        e.stsPrdDplymntPlanned === 0
          ? "Done"
          : e.stsPrdDplymntPlanned.toString();
      ifResult.stsCompleted = e.stsCompleted.toString();
      return ifResult;
    });
    return {
      ifSpreadSheet: ifSpreadsheetRecordData,
    };
  }, [pageState.projectTickets.interfaceDefinitions]);

  const cpntSpreadsheetInitialData = useMemo(() => {
    const cnptSpreadsheetRecordData: CnptSpreadsheetRecordData[] =
      pageState.projectTickets.componentProjects.map((e) => {
        const cpntResult: CnptSpreadsheetRecordData = {
          pjtId: e.pjtId,
          pjtName: e.pjtName,
          stgSchedDplymntDate: e.stgSchedDplymntDate,
          prdSchedDplymntDate: e.prdSchedDplymntDate,
          dipByRgn: e.dipByRgn,
          cpntId: e.cpntId,
          cpntPjtTypeName: e.cpntPjtTypeName,
          inOutTypeName: e.inOutTypeName,
          prtcolTypeName: e.prtcolTypeName,
          reqTskSlctdFlg: e.reqTskSlctdFlg,
          stgIfdDplymntPsblFlg: e.stgIfdDplymntPsblFlg,
          prdIfdDplymntPsblFlg: e.prdIfdDplymntPsblFlg,
          completedFlg: e.completedFlg,
        };
        return {
          ...ProjectDetailPageInitialState.cpntSpreadSheet.tmplateRow,
          ...cpntResult,
        };
      });
    return {
      cpntSpreadSheet: cnptSpreadsheetRecordData,
    };
  }, [pageState.projectTickets.componentProjects]);

  const onCellClicked = (evt: CustomEvent) => {
    const eventName = evt.detail.name;
    const selectedRow = evt.detail.value;
    let href = "";
    console.log("selectedRow", selectedRow);
    switch (eventName) {
      case "groupTag":
        if (
          pageState.projects.pjtLnchTypeCd === PjtLnchType.GOD &&
          isDisplayedAction("interface_list_reference.button.god")
        ) {
          navigate(
            `/interface-list?pjtId=${appState.pjtId}&mode=god&type=${ReferenceModeType.ACTIVE_PROJECTS}`,
          );
          break;
        }
        navigate(`/interface-list?mode=input&pjtId=${appState.pjtId}`);
        break;
      case "groupTag_newTabIcon":
        if (
          pageState.projects.pjtLnchTypeCd === PjtLnchType.GOD &&
          isDisplayedAction("interface_list_reference.button.god")
        ) {
          href = createPath({
            pathname: `/interface-list?pjtId=${appState.pjtId}&mode=god&type=${ReferenceModeType.ACTIVE_PROJECTS}`,
          });
          window.open(window.location.pathname + "#" + href);
          break;
        }
        href = createPath({
          pathname: `/interface-list?mode=input&pjtId=${appState.pjtId}`,
        });
        window.open(window.location.pathname + "#" + href);
        break;
      case "pjtName":
        navigate(`/component-project-detail?cpntPjtId=${selectedRow.pjtId}`);
        break;
      case "pjtName_newTabIcon":
        href = createPath({
          pathname: `/component-project-detail?cpntPjtId=${selectedRow.pjtId}`,
        });
        window.open(window.location.pathname + "#" + href);
        break;
    }
  };

  return (
    <>
      <div style={{ marginLeft: "30px" }}>
        <div style={{ position: "relative" }}>
          <FrVrfTitle content="Interface Definitions" />
          <FrVrfTextDisplayField
            style={{
              "--vrf-text-display-field-font-size": "12px",
              textAlign: "right",
              position: "absolute",
              bottom: "16px",
              right: "60px",
            }}
            text="* The number of IFs that are not ready to deploy because component definitions are not deployed."
          />
          <FrVrfTextDisplayField
            style={{
              "--vrf-text-display-field-font-size": "12px",
              textAlign: "right",
              position: "absolute",
              bottom: "0px",
              right: "60px",
            }}
            text="Please contact to IF platform members to confirm component definition deployment schedule."
          />
        </div>
        <FrVrfSpreadsheet
          data={ifSpreadsheetInitialData}
          templateUrl={TEMPLATE_INTERFACE_PROGRESS_SUMMARY}
          onInitialized={initIfSpreadSheet}
          onCellClicked={onCellClicked}
          maxHeight={300}
        />
        <FrVrfTitle content="Component Projects" />
        <FrVrfSpreadsheet
          data={cpntSpreadsheetInitialData}
          templateUrl={TEMPLATE_COMPONENT_PROGRESS_SUMMARY}
          onInitialized={initCpntSpreadSheet}
          onCellClicked={onCellClicked}
          maxHeight={-90}
        />
        <FrVrfFooter>
          <FrVrfButton
            content="Reload"
            size="large"
            onClicked={() => onInitialDisplayed()}
            authActId="project_plan.button.reload"
          />
        </FrVrfFooter>
      </div>
    </>
  );
};
